"use client";

/**
 * captureALL Phase 3 — InlineFanoutPanel
 *
 * Renders 2–4 per-row proposal cards from a chat fan-out response.
 * Each row has its own commit / edit / dismiss affordances and an
 * independent state machine: idle → saving → saved | failed.
 *
 * When ALL rows reach 'saved', the panel calls onAllSaved() with a
 * summary; ChatScreen owns the success toast. Escape calls onDismissPanel().
 *
 * ADR-0006: this panel is ONLY for proposal_mode === "inline" (≤4 items).
 * The modal branch (≥5 items) is reserved for Phase 4.
 */

import { useEffect, useReducer, useRef, useCallback } from "react";
import type { AppProposal, ActionReveal, CaptureRouteProposal } from "@/lib/api";
import { routeCapture, commitCapture } from "@/lib/api";
import { notifyFromActionReveal } from "@/lib/store";
import { surfaceLabel } from "@/lib/stores/chatReveal";
import { useFocusTrap } from "@/lib/useFocusTrap";

// ── Types ─────────────────────────────────────────────────────────────────────

type RowState = "idle" | "saving" | "saved" | "failed" | "dismissed";

/** Keyed by proposal index (not id — ids may be non-integer strings). */
interface RowMeta {
  state: RowState;
  editText: string;
  isEditing: boolean;
  errorMessage: string | null;
  /** Populated once commit succeeds — used to build the success toast. */
  reveal: ActionReveal | null;
}

interface PanelState {
  rows: RowMeta[];
}

type PanelAction =
  | { type: "SET_SAVING"; idx: number }
  | { type: "SET_SAVED"; idx: number; reveal: ActionReveal | null }
  | { type: "SET_FAILED"; idx: number; message: string }
  | { type: "DISMISS_ROW"; idx: number }
  | { type: "START_EDIT"; idx: number }
  | { type: "COMMIT_EDIT"; idx: number; text: string }
  | { type: "CANCEL_EDIT"; idx: number };

function buildInitialState(proposals: AppProposal[]): PanelState {
  return {
    rows: proposals.map((p) => ({
      state: "idle",
      editText: p.label,
      isEditing: false,
      errorMessage: null,
      reveal: null,
    })),
  };
}

function panelReducer(state: PanelState, action: PanelAction): PanelState {
  const updateRow = (idx: number, patch: Partial<RowMeta>): PanelState => ({
    rows: state.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  });

  switch (action.type) {
    case "SET_SAVING":
      return updateRow(action.idx, { state: "saving", errorMessage: null });
    case "SET_SAVED":
      return updateRow(action.idx, { state: "saved", reveal: action.reveal });
    case "SET_FAILED":
      return updateRow(action.idx, {
        state: "failed",
        errorMessage: action.message,
      });
    case "DISMISS_ROW":
      // Client-side dismissal — a distinct terminal state, NOT 'saved'.
      // Dismissed rows are excluded from the "Saved N" toast count so the
      // success message never overstates what was committed (3-layer
      // contract: the UI promise must match the durable effect).
      // Phase 4: wire to dismissCaptureFanout() endpoint.
      return updateRow(action.idx, {
        state: "dismissed",
        reveal: null,
        errorMessage: null,
      });
    case "START_EDIT":
      return updateRow(action.idx, { isEditing: true });
    case "COMMIT_EDIT":
      return updateRow(action.idx, {
        isEditing: false,
        editText: action.text,
      });
    case "CANCEL_EDIT":
      return updateRow(action.idx, { isEditing: false });
    default:
      return state;
  }
}

// ── target_kind → ActionReveal.surface map ────────────────────────────────────

const KIND_TO_SURFACE: Record<string, ActionReveal["surface"]> = {
  task: "today",
  schedule_block: "plan",
  decision: "decisions",
  reference: "library",
  capture_only: "reflect",
  goal: "goals",
};

function kindToSurface(kind: string): ActionReveal["surface"] {
  return KIND_TO_SURFACE[kind] ?? "today";
}

// ── buildCommitConfirmation (replicated from TodayScreen pattern) ─────────────

function buildCommitConfirmation(proposal: CaptureRouteProposal) {
  return {
    decision: "confirm" as const,
    proposal_id: proposal.id,
    target_kind: proposal.kind,
    authority_level: proposal.authority_level,
    destination_layer: proposal.destination_layer,
    owning_service: "capture_routing" as const,
  };
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface InlineFanoutPanelProps {
  proposals: AppProposal[];
  onAllSaved: (summary: { total: number; reveals: ActionReveal[] }) => void;
  onDismissPanel: () => void;
  /**
   * "inline" (default, ADR-0006 ≤4 items) renders a chat-embedded section.
   * "modal" (≥5 items) renders the SAME rows + logic inside a scrollable
   * dialog with a "Commit all" action — so large brain dumps are usable.
   */
  variant?: "inline" | "modal";
}

// ── InlineFanoutPanel ─────────────────────────────────────────────────────────

export function InlineFanoutPanel({
  proposals,
  onAllSaved,
  onDismissPanel,
  variant = "inline",
}: InlineFanoutPanelProps) {
  const [state, dispatch] = useReducer(
    panelReducer,
    proposals,
    buildInitialState
  );

  const onAllSavedRef = useRef(onAllSaved);
  onAllSavedRef.current = onAllSaved;
  const onDismissPanelRef = useRef(onDismissPanel);
  onDismissPanelRef.current = onDismissPanel;

  // Track whether any row is mid-edit so the panel-level Escape handler can
  // stand down — Escape during an edit should cancel that edit only (handled
  // by the input's own keydown), never discard every row.
  const anyEditing = state.rows.some((r) => r.isEditing);
  const anyEditingRef = useRef(anyEditing);
  anyEditingRef.current = anyEditing;

  // Escape key dismisses the panel without committing — but not while editing.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !anyEditingRef.current) {
        onDismissPanelRef.current();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Modal a11y (Phase 5): trap Tab focus inside the dialog + restore focus on
  // close (shared useFocusTrap hook, same as ChatSheet/SettingsSheet). We pass
  // NO onEscape — the edit-aware Escape handler above stays authoritative so
  // Escape-during-edit cancels the edit, not the panel (the M1 fix). No-op for
  // the inline variant.
  const modalCardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(variant === "modal", modalCardRef);

  // Scroll-lock the page body while the modal is open so background content
  // doesn't scroll under the overlay.
  useEffect(() => {
    if (variant !== "modal") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [variant]);

  // Settle the panel once no row is still pending. A row is "settled" when
  // it is either committed ('saved') or 'dismissed'; a 'failed' or in-flight
  // row keeps the panel open so the user can retry. The toast counts ONLY
  // saved rows — dismissed rows never inflate "Saved N".
  useEffect(() => {
    if (state.rows.length === 0) return;
    const settled = state.rows.every(
      (r) => r.state === "saved" || r.state === "dismissed"
    );
    if (!settled) return;
    const savedRows = state.rows.filter((r) => r.state === "saved");
    if (savedRows.length > 0) {
      const reveals = savedRows
        .map((r) => r.reveal)
        .filter((rv): rv is ActionReveal => rv !== null);
      onAllSavedRef.current({ total: savedRows.length, reveals });
    } else {
      // Every row dismissed, nothing committed → close the panel silently.
      onDismissPanelRef.current();
    }
  }, [state.rows]);

  // First-time hint
  const hintSeen =
    typeof window !== "undefined"
      ? localStorage.getItem("captureall_fanout_hint_seen") === "1"
      : false;

  const handleDismissHint = useCallback(() => {
    localStorage.setItem("captureall_fanout_hint_seen", "1");
    // Force re-render by dispatching a neutral action; simpler than extra state.
    // Phase 4: migrate to MemoryRecord(feature_seen, fanout_hint)
    dispatch({ type: "CANCEL_EDIT", idx: -1 } as unknown as PanelAction);
  }, []);

  const handleCommit = useCallback(
    async (idx: number) => {
      const proposal = proposals[idx];
      const row = state.rows[idx];
      if (!proposal || row.state === "saving" || row.state === "saved") return;

      dispatch({ type: "SET_SAVING", idx });

      try {
        // Step 1: get the current route state (TodayScreen pattern)
        const routeState = await routeCapture(proposal.capture_id);

        // Step 2: find matching proposal for target_kind (fall back to first)
        const matched: CaptureRouteProposal | undefined =
          routeState.proposals.find(
            (p) => p.kind === proposal.target_kind
          ) ?? routeState.proposals[0];

        // Guard: routing returned no proposals — surface a clean failed row
        // instead of throwing on `matched.kind`.
        if (!matched) {
          dispatch({
            type: "SET_FAILED",
            idx,
            message: "Couldn't route this item — try again.",
          });
          return;
        }

        // Step 3: commit with the full confirmation envelope.
        // An edited label is a title override. The backend
        // (capture_routing.commit_capture_item) reads the committed object's
        // title from `payload.title` for task / schedule_block / goal /
        // reference, and from `payload.question` for decision. Send under the
        // matching key so the edit is honored rather than silently dropped.
        const editedText = row.editText;
        const titleKey = matched.kind === "decision" ? "question" : "title";
        const commitResult = await commitCapture(proposal.capture_id, {
          target_kind: matched.kind,
          confirmation: buildCommitConfirmation(matched),
          payload:
            editedText !== proposal.label
              ? { [titleKey]: editedText }
              : undefined,
        });

        // Build ActionReveal from the COMMIT response. reveal.id must be the
        // created object's id (commitment.target_id), NOT the capture id —
        // otherwise the "Open in {Surface}" deep-link can't focus the new row.
        const surface = kindToSurface(matched.kind);
        const reveal: ActionReveal = {
          surface,
          id: commitResult.commitment.target_id,
          label: editedText,
        };

        // Sync-side wiring (wiring-map §3e L7): the durable entity now exists,
        // so fire the matching notify*Change for its surface. Without this an
        // already-mounted Today/Plan/Goals tab stayed stale until remount — the
        // committed task/goal/block was invisible unless the user clicked the
        // toast deep-link. notifyFromActionReveal maps surface →
        // notifyTask/Goal/ScheduleChange, each of which invalidates the
        // projection caches so the surface refetches. Only on the SUCCESS path —
        // a failed commit falls through to SET_FAILED and notifies nothing.
        notifyFromActionReveal(reveal);

        dispatch({ type: "SET_SAVED", idx, reveal });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Could not save — try again.";
        dispatch({ type: "SET_FAILED", idx, message });
      }
    },
    [proposals, state.rows]
  );

  // Modal "Commit all": commit every not-yet-terminal row, SEQUENTIALLY, so a
  // 15-item dump doesn't fire 15 concurrent route+commit pairs. Each row that
  // fails stays 'failed' for individual retry; the rest still go through.
  const handleCommitAll = useCallback(async () => {
    for (let idx = 0; idx < state.rows.length; idx++) {
      const rowState = state.rows[idx].state;
      if (rowState === "idle" || rowState === "failed") {
        await handleCommit(idx);
      }
    }
  }, [state.rows, handleCommit]);

  const handleDismissRow = useCallback((idx: number) => {
    dispatch({ type: "DISMISS_ROW", idx });
  }, []);

  const handleStartEdit = useCallback((idx: number) => {
    dispatch({ type: "START_EDIT", idx });
  }, []);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
      if (e.key === "Enter") {
        dispatch({
          type: "COMMIT_EDIT",
          idx,
          text: (e.target as HTMLInputElement).value,
        });
      } else if (e.key === "Escape") {
        // Stop the panel-level Escape listener from also firing (belt-and-
        // suspenders alongside the anyEditing guard) so the panel stays open.
        e.stopPropagation();
        dispatch({ type: "CANCEL_EDIT", idx });
      }
    },
    []
  );

  const handleEditBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>, idx: number) => {
      dispatch({ type: "COMMIT_EDIT", idx, text: e.target.value });
    },
    []
  );

  // Hint visibility is derived from localStorage — re-check on render.
  const showHint =
    typeof window !== "undefined"
      ? localStorage.getItem("captureall_fanout_hint_seen") !== "1"
      : false;
  // Suppress hint-based re-renders from CANCEL_EDIT(-1) sentinel trick.
  void hintSeen;

  const pendingCount = state.rows.filter(
    (r) => r.state === "idle" || r.state === "failed"
  ).length;

  const inner = (
    <>
      {/* First-time hint */}
      {showHint && (
        <div
          data-testid="fanout-hint"
          className="flex items-center justify-between gap-2 border-b border-border
                     px-4 py-2 text-[11px] text-text-light"
        >
          <span>
            Tap <strong>Commit</strong> to save each item, or edit the label
            first.
          </span>
          <button
            type="button"
            data-testid="fanout-hint-dismiss"
            onClick={handleDismissHint}
            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium
                       text-muted hover:text-text transition-colors"
            aria-label="Dismiss hint"
          >
            Got it
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted">
          {proposals.length} item{proposals.length !== 1 ? "s" : ""} captured
        </p>
        <button
          type="button"
          onClick={onDismissPanel}
          aria-label="Dismiss all proposals"
          className="rounded-full p-1 text-muted hover:text-text transition-colors
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Rows */}
      <ul className="divide-y divide-border/60 px-2 pb-2" role="list">
        {proposals.map((proposal, idx) => (
          <FanoutRow
            key={proposal.id}
            idx={idx}
            proposal={proposal}
            rowMeta={state.rows[idx]}
            onCommit={handleCommit}
            onDismiss={handleDismissRow}
            onStartEdit={handleStartEdit}
            onEditKeyDown={handleEditKeyDown}
            onEditBlur={handleEditBlur}
          />
        ))}
      </ul>

      {/* Modal-only: commit every remaining row in one tap. */}
      {variant === "modal" && pendingCount > 0 && (
        <div
          className="sticky bottom-0 flex items-center justify-between gap-2
                     border-t border-border bg-cream/95 px-4 py-3 backdrop-blur"
        >
          <span className="text-[11px] text-muted">
            {pendingCount} left to review
          </span>
          <button
            type="button"
            data-testid="fanout-commit-all"
            onClick={handleCommitAll}
            className="rounded-full bg-forest-med px-4 py-1.5 text-[12px] font-medium
                       text-white transition-colors hover:bg-forest
                       motion-reduce:transition-none focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-forest-med/40"
          >
            Commit all {pendingCount}
          </button>
        </div>
      )}
    </>
  );

  if (variant === "modal") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Review captured items"
        data-testid="fanout-modal-panel"
        onClick={(e) => {
          // Backdrop click dismisses; clicks inside the card do not.
          if (e.target === e.currentTarget) onDismissPanel();
        }}
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/30
                   p-0 sm:items-center sm:p-4"
      >
        <div
          ref={modalCardRef}
          className="flex max-h-[80vh] w-full flex-col overflow-y-auto rounded-t-2xl
                     border border-border bg-cream shadow-lg sm:max-w-lg sm:rounded-2xl"
        >
          {inner}
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="fanout-inline-panel"
      aria-label="Review captured items"
      className="mx-4 mb-2 rounded-xl border border-border bg-sage-pale shadow-sm"
    >
      {inner}
    </section>
  );
}

// ── FanoutRow ─────────────────────────────────────────────────────────────────

interface FanoutRowProps {
  idx: number;
  proposal: AppProposal;
  rowMeta: RowMeta;
  onCommit: (idx: number) => void;
  onDismiss: (idx: number) => void;
  onStartEdit: (idx: number) => void;
  onEditKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => void;
  onEditBlur: (e: React.FocusEvent<HTMLInputElement>, idx: number) => void;
}

function FanoutRow({
  idx,
  proposal,
  rowMeta,
  onCommit,
  onDismiss,
  onStartEdit,
  onEditKeyDown,
  onEditBlur,
}: FanoutRowProps) {
  const isSaved = rowMeta.state === "saved";
  const isFailed = rowMeta.state === "failed";
  const isSaving = rowMeta.state === "saving";
  const isDismissed = rowMeta.state === "dismissed";
  const isTerminal = isSaved || isDismissed;

  return (
    <li
      data-testid={`fanout-row-${idx}`}
      data-state={rowMeta.state}
      className={`group flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors
        ${isSaved ? "opacity-60" : ""}
        ${isDismissed ? "opacity-45" : ""}
        ${isFailed ? "bg-amber/[0.06] border border-amber/20 rounded-lg" : ""}
      `}
    >
      {/* Surface badge */}
      <span
        className="mt-0.5 shrink-0 rounded-full bg-forest-med/10 px-2 py-0.5
                   text-[10px] font-medium uppercase tracking-[0.1em] text-forest-med"
        aria-label={`Surface: ${surfaceLabel(kindToSurface(proposal.target_kind))}`}
      >
        {surfaceLabel(kindToSurface(proposal.target_kind))}
      </span>

      {/* Label / edit input */}
      <div className="min-w-0 flex-1">
        {rowMeta.isEditing ? (
          <input
            type="text"
            defaultValue={rowMeta.editText}
            autoFocus
            onKeyDown={(e) => onEditKeyDown(e, idx)}
            onBlur={(e) => onEditBlur(e, idx)}
            className="w-full rounded border border-forest-med/30 bg-white px-2 py-0.5
                       text-sm text-text focus:outline-none focus:ring-2
                       focus:ring-forest-med/40"
            aria-label="Edit label"
          />
        ) : (
          <p
            className={`text-sm font-medium leading-snug ${
              isSaved
                ? "line-through text-muted"
                : isDismissed
                  ? "text-muted"
                  : "text-text"
            }`}
          >
            {rowMeta.editText}
          </p>
        )}

        {/* Inline amber error copy — never red/rose */}
        {isFailed && rowMeta.errorMessage && (
          <p
            data-testid="fanout-row-error"
            className="mt-1 text-[11px] text-amber"
            role="alert"
          >
            {rowMeta.errorMessage}
          </p>
        )}
      </div>

      {/* Action buttons — hidden once the row reaches a terminal state */}
      {!isTerminal && (
        <div className="flex shrink-0 items-center gap-1">
          {/* Edit */}
          {!rowMeta.isEditing && (
            <button
              type="button"
              data-testid="fanout-row-edit"
              onClick={() => onStartEdit(idx)}
              disabled={isSaving}
              aria-label="Edit label"
              className="rounded-full px-2 py-1 text-[10px] font-medium text-muted
                         hover:text-text transition-colors
                         disabled:opacity-40
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
            >
              Edit
            </button>
          )}

          {/* Dismiss */}
          <button
            type="button"
            data-testid="fanout-row-dismiss"
            onClick={() => onDismiss(idx)}
            disabled={isSaving}
            aria-label="Dismiss this item"
            className="rounded-full px-2 py-1 text-[10px] font-medium text-muted
                       hover:text-text transition-colors
                       disabled:opacity-40
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
          >
            Dismiss
          </button>

          {/* Commit — primary CTA */}
          <button
            type="button"
            data-testid="fanout-row-commit"
            onClick={() => onCommit(idx)}
            disabled={isSaving}
            aria-label={`Commit: ${rowMeta.editText}`}
            className="rounded-full bg-forest-med px-3 py-1 text-[11px] font-medium
                       text-white transition-colors hover:bg-forest
                       disabled:opacity-50
                       motion-reduce:transition-none
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-med/40"
          >
            {isSaving ? "Saving…" : isFailed ? "Retry" : "Commit"}
          </button>
        </div>
      )}

      {/* Dismissed indicator — muted, no checkmark (it was NOT saved) */}
      {isDismissed && (
        <span className="shrink-0 self-center text-[10px] font-medium uppercase tracking-[0.1em] text-muted">
          Dismissed
        </span>
      )}

      {/* Saved checkmark */}
      {isSaved && (
        <span
          aria-hidden="true"
          className="shrink-0 rounded-full bg-accent/15 p-0.5 text-accent"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path
              d="M5 12l5 5L20 7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </li>
  );
}
